import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import StatsCard from '../components/StatsCard';
import DateRangePicker from '../components/DateRangePicker';
import {
  Download,
  AlertCircle,
  Eye,
  MousePointerClick,
  CheckCircle,
  BarChart,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  FileSpreadsheet,
  ChevronDown,
  Search,
  UserMinus,
  Copy,
  Check
} from 'lucide-react';
import {
  BarChart as RechartsBarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts';

const SES_COLS = [
  { label: 'Send Volume', tip: 'Total emails submitted to SES for delivery in this period.' },
  { label: 'Delivered %', tip: 'Percentage of sends that were accepted by the recipient\'s mail server. High is good — aim for >95%.' },
  { label: 'Transient Bounce %', tip: 'Soft bounces: temporary failures like full mailbox or server unavailable. The recipient\'s server accepted the message but couldn\'t deliver it right now. Should stay below 5%.' },
  { label: 'Perm. Bounce %', tip: 'Hard bounces: the email address doesn\'t exist or the domain has no mail server. These addresses are permanently undeliverable. Keep below 2% — AWS will pause sending if this exceeds 10%.' },
  { label: 'Complaints %', tip: 'Recipients who marked your email as spam. Even 0.1% can trigger deliverability issues. Keep as close to 0% as possible — AWS pauses sending above 0.5%.' },
  { label: 'Opens %', tip: 'Percentage of delivered emails that were opened. Tracked via a 1×1 pixel image. Note: Apple Mail Privacy Protection pre-loads this pixel, which can inflate open rates for Apple Mail users.' },
  { label: 'Clicks %', tip: 'Percentage of delivered emails where a recipient clicked at least one tracked link.' },
];

const SesColHeader: React.FC<{ label: string; tip: string }> = ({ label, tip }) => {
  const [pos, setPos] = React.useState<{ top: number; left: number; arrowOffset: number } | null>(null);
  const iconRef = React.useRef<HTMLSpanElement>(null);

  const handleEnter = () => {
    if (iconRef.current) {
      const r = iconRef.current.getBoundingClientRect();
      const tipWidth = 256;
      const centeredLeft = r.left + r.width / 2 - tipWidth / 2;
      const clampedLeft = Math.min(centeredLeft, window.innerWidth - tipWidth - 12);
      const arrowOffset = r.left + r.width / 2 - clampedLeft;
      setPos({ top: r.top - 8, left: clampedLeft, arrowOffset });
    }
  };

  const tooltip = pos ? createPortal(
    <div
      className="fixed z-[9999] w-64 bg-slate-900 text-white text-[11px] leading-relaxed rounded-xl px-3 py-2.5 shadow-xl border border-white/10 font-normal text-left pointer-events-none"
      style={{ top: pos.top, left: pos.left, transform: 'translateY(-100%)' }}
    >
      {tip}
      <div className="absolute top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-slate-900" style={{ left: pos.arrowOffset - 4 }} />
    </div>,
    document.body
  ) : null;

  return (
    <th className="py-3 px-4 text-right">
      <div className="inline-flex items-center gap-1 justify-end">
        <span>{label}</span>
        <span
          ref={iconRef}
          className="cursor-help text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          onMouseEnter={handleEnter}
          onMouseLeave={() => setPos(null)}
        >
          <svg className="w-3 h-3" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 3a.75.75 0 110 1.5A.75.75 0 018 4zm-.25 3h1.5v4.5h-1.5V7z"/>
          </svg>
        </span>
      </div>
      {tooltip}
    </th>
  );
};

export const Analytics: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'campaign-reports' | 'ses-metrics'>(
    (searchParams.get('tab') as 'campaign-reports' | 'ses-metrics') || 'campaign-reports'
  );
  const [selectedCampaignId, setSelectedCampaignId] = useState(searchParams.get('campaign') || '');
  const [logPage, setLogPage] = useState(1);
  const [logSearch, setLogSearch] = useState('');
  const [logStatus, setLogStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
  const [copiedId, setCopiedId] = useState('');
  const [sesDateRange, setSesDateRange] = useState<'7d' | '14d' | '30d' | 'all' | 'custom'>(
    (searchParams.get('range') as '7d' | '14d' | '30d' | 'all' | 'custom') || '30d'
  );
  const [sesCustomStart, setSesCustomStart] = useState(searchParams.get('cstart') || '');
  const [sesCustomEnd, setSesCustomEnd] = useState(searchParams.get('cend') || new Date().toISOString().slice(0, 10));
  const [sesSubTab, setSesSubTab] = useState<'identities' | 'isp'>(
    (searchParams.get('view') as 'identities' | 'isp') || 'identities'
  );

  const updateSearchParams = (updates: Record<string, string>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => v ? next.set(k, v) : next.delete(k));
      return next;
    }, { replace: true });
  };
  const searchRef = useRef<HTMLDivElement>(null);
  const inputWrapRef = useRef<HTMLDivElement>(null);

  const updateRect = useCallback(() => {
    if (inputWrapRef.current) setDropdownRect(inputWrapRef.current.getBoundingClientRect());
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inInput = searchRef.current?.contains(target);
      const inPortal = document.getElementById('campaign-dropdown-portal')?.contains(target);
      if (!inInput && !inPortal) {
        setDropdownOpen(false);
        setSearchInput('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!dropdownOpen) return;
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [dropdownOpen, updateRect]);

  const { data: campaignsRes } = useQuery({
    queryKey: ['campaignsDropdown'],
    queryFn: async () => {
      const res = await apiClient.get('/campaigns', { params: { limit: 100 } });
      return res.data;
    }
  });

  const { data: dashboardStats } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      const res = await apiClient.get('/analytics/dashboard');
      return res.data;
    },
    enabled: !selectedCampaignId
  });

  const sesStartDate = (() => {
    if (sesDateRange === 'all' || sesDateRange === 'custom') return null;
    const days = sesDateRange === '7d' ? 7 : sesDateRange === '14d' ? 14 : 30;
    return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  })();
  const sesEndDate = new Date().toISOString();

  const { data: sesMetrics, isLoading: sesLoading, error: sesError } = useQuery({
    queryKey: ['sesMetrics', sesDateRange, sesCustomStart, sesCustomEnd],
    queryFn: async () => {
      let params: Record<string, string>;
      if (sesDateRange === 'all') {
        params = { allTime: 'true' };
      } else if (sesDateRange === 'custom') {
        if (!sesCustomStart || !sesCustomEnd) return { identityStats: [], ispStats: [] };
        params = { startDate: new Date(sesCustomStart).toISOString(), endDate: new Date(sesCustomEnd).toISOString() };
      } else {
        params = { startDate: sesStartDate!, endDate: sesEndDate };
      }
      const res = await apiClient.get('/analytics/ses-metrics', { params });
      return res.data;
    },
    enabled: activeTab === 'ses-metrics' && (sesDateRange !== 'custom' || (!!sesCustomStart && !!sesCustomEnd))
  });

  const { data: analyticsData, isLoading, error } = useQuery({
    queryKey: ['campaignAnalytics', selectedCampaignId, logPage, logSearch, logStatus],
    queryFn: async () => {
      if (!selectedCampaignId) return null;
      const res = await apiClient.get(`/analytics/campaign/${selectedCampaignId}`, {
        params: { page: logPage, limit: 10, search: logSearch, status: logStatus }
      });
      return res.data;
    },
    enabled: !!selectedCampaignId
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLogSearch(e.target.value);
    setLogPage(1);
  };

  const handleCopyEmail = (email: string, id: string) => {
    navigator.clipboard.writeText(email);
    setCopiedId(id);
    setTimeout(() => setCopiedId(''), 1500);
  };

  const handleExportCSV = async () => {
    if (!selectedCampaignId) return;
    try {
      const res = await apiClient.get(`/analytics/campaign/${selectedCampaignId}/export`, {
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(res.data);
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yy = String(now.getFullYear()).slice(-2);
      const safeName = (selectedCampaign?.name || selectedCampaignId)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${safeName}_${dd}${mm}${yy}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('CSV export failed:', err);
    }
  };

  const campaigns = campaignsRes?.campaigns || [];
  const campaign = analyticsData?.campaign || {};
  const stats = analyticsData?.stats || {};
  const logs = analyticsData?.logs?.recipientLogs || [];
  const pagination = analyticsData?.logs?.pagination || { total: 0, pages: 1 };

  const selectedCampaign = campaigns.find((c: any) => c.id === selectedCampaignId);
  const inputDisplayValue = dropdownOpen ? searchInput : (selectedCampaign ? selectedCampaign.name : '');
  const filteredCampaigns = campaigns.filter((c: any) =>
    !searchInput || c.name.toLowerCase().includes(searchInput.toLowerCase())
  );

  const METRIC_COLORS: Record<string, string> = {
    Sent: '#DC5829', Delivered: '#10b981', Opens: '#10b981',
    Clicks: '#06b6d4', Bounces: '#f43f5e', Complaints: '#f97316', Unsubscribes: '#f59e0b'
  };

  const chartData = stats.total
    ? [
        { name: 'Sent',         value: stats.sent || 0,          fill: METRIC_COLORS.Sent         },
        { name: 'Delivered',    value: stats.delivered || 0,     fill: METRIC_COLORS.Delivered    },
        { name: 'Opens',        value: stats.opened || 0,        fill: METRIC_COLORS.Opens        },
        { name: 'Clicks',       value: stats.clicked || 0,       fill: METRIC_COLORS.Clicks       },
        { name: 'Bounces',      value: stats.bounced || 0,       fill: METRIC_COLORS.Bounces      },
        { name: 'Complaints',   value: stats.complained || 0,    fill: METRIC_COLORS.Complaints   },
        { name: 'Unsubscribes', value: stats.unsubscribed || 0,  fill: METRIC_COLORS.Unsubscribes },
      ]
    : [];

  const labelStyle = { fontSize: 10, fill: '#64748b' };

  const sentCount      = stats.sent || 0;
  const deliveredCount = stats.delivered || 0;

  const funnelSteps = [
    { label: 'Sent',      count: sentCount,            bg: 'bg-primary-500',    prevCount: null as null | number, convLabel: null as null | string, base: null as null | number, baseLabel: null as null | string },
    { label: 'Delivered', count: deliveredCount,       bg: 'bg-emerald-500', prevCount: sentCount,             convLabel: 'of sent',      base: sentCount,      baseLabel: 'of sent'      },
    { label: 'Opened',    count: stats.opened || 0,    bg: 'bg-primary-500',  prevCount: deliveredCount,        convLabel: 'of delivered', base: deliveredCount, baseLabel: 'of delivered' },
    { label: 'Clicked',   count: stats.clicked || 0,   bg: 'bg-cyan-500',    prevCount: stats.opened || 0,     convLabel: 'of opened',    base: deliveredCount, baseLabel: 'of delivered' },
  ];

  const sesIdentityStats: any[] = (sesMetrics?.identityStats || []).sort((a: any, b: any) => b.send - a.send);
  const sesIspStats: any[] = (sesMetrics?.ispStats || []).sort((a: any, b: any) => b.send - a.send);

  const rateCell = (val: number, color: string, warnAbove?: number) => {
    const cls = warnAbove !== undefined && val > warnAbove ? 'text-rose-500' : color;
    return <span className={`font-semibold tabular-nums ${cls}`}>{val.toFixed(2)}%</span>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase">Analytics</h2>
        <p className="text-xs text-slate-550 dark:text-slate-400">Inspect deliverability rates, link tracking, and subscriber events.</p>
      </div>

      <div className="flex gap-2">
        {([
          { key: 'campaign-reports', label: 'Campaign Reports' },
          { key: 'ses-metrics', label: 'SES Metrics' }
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => { const t = tab.key as 'campaign-reports' | 'ses-metrics'; setActiveTab(t); updateSearchParams({ tab: t }); }}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              activeTab === tab.key
                ? 'bg-primary-500/15 text-primary-600 dark:text-primary-400 border-primary-500/40'
                : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:border-primary-300 dark:hover:border-white/20'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'ses-metrics' && (
        <div className="space-y-6 animate-fade-in">
          <div className="glass-panel rounded-2xl p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-2 items-center">
              {([
                { key: '7d', label: 'Last 7 days' },
                { key: '14d', label: 'Last 14 days' },
                { key: '30d', label: 'Last 30 days' },
                { key: 'all', label: 'All Time' },
                { key: 'custom', label: 'Custom' }
              ]).map(p => (
                <button
                  key={p.key}
                  onClick={() => { const r = p.key as '7d' | '14d' | '30d' | 'all' | 'custom'; setSesDateRange(r as any); updateSearchParams({ range: r }); }}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                    sesDateRange === p.key
                      ? 'bg-primary-500/15 text-primary-600 dark:text-primary-400 border-primary-500/40'
                      : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:border-primary-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {sesDateRange === 'custom' && (
                <DateRangePicker
                  startDate={sesCustomStart}
                  endDate={sesCustomEnd}
                  onApply={(start, end) => { setSesCustomStart(start); setSesCustomEnd(end); }}
                  maxDate={new Date().toISOString().slice(0, 10)}
                />
              )}
            </div>
            <div className="flex gap-2">
              {([
                { key: 'identities', label: 'Sending Identities' },
                { key: 'isp', label: 'ISP Breakdown' }
              ]).map(p => (
                <button
                  key={p.key}
                  onClick={() => { const v = p.key as 'identities' | 'isp'; setSesSubTab(v); updateSearchParams({ view: v }); }}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                    sesSubTab === p.key
                      ? 'bg-primary-500/15 text-primary-600 dark:text-primary-400 border-primary-500/40'
                      : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:border-primary-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {sesLoading && (
            <div className="py-12 text-center">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <span className="text-xs text-slate-500 block mt-3">Fetching VDM metrics...</span>
            </div>
          )}

          {sesError ? (
            <div className="p-6 bg-accent-rose/10 border border-accent-rose/20 rounded-xl text-accent-rose text-sm">
              {(sesError as any)?.response?.data?.error || 'Failed to load SES metrics. Ensure VDM is enabled in your AWS SES account.'}
            </div>
          ) : null}

          {!sesLoading && !sesError && sesSubTab === 'identities' && (
            <div className="glass-panel rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-white/5">
                <h4 className="text-xs font-bold text-slate-800 dark:text-white uppercase tracking-wider">Sending Identities</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-white/5 text-slate-400 text-[11px] font-semibold uppercase tracking-wider">
                      <th className="py-3 px-6">Identity</th>
                      {SES_COLS.map(col => <SesColHeader key={col.label} {...col} />)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-white/5 text-sm text-slate-600 dark:text-slate-300">
                    {sesIdentityStats.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-8 text-slate-500 text-xs">No data for this period.</td></tr>
                    ) : sesIdentityStats.map((row: any) => (
                      <tr key={row.identity} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                        <td className="py-3.5 px-6 font-mono text-xs text-slate-700 dark:text-slate-200">{row.identity}</td>
                        <td className="py-3.5 px-4 text-right tabular-nums">{(row.send || 0).toLocaleString()}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.deliveryRate, 'text-emerald-500')}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.transientBounceRate, 'text-slate-500', 5)}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.permanentBounceRate, 'text-slate-500', 5)}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.complaintRate, 'text-slate-500', 0.1)}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.openRate, 'text-primary-500')}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.clickRate, 'text-cyan-500')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!sesLoading && !sesError && sesSubTab === 'isp' && (
            <div className="glass-panel rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-white/5">
                <h4 className="text-xs font-bold text-slate-800 dark:text-white uppercase tracking-wider">ISP Breakdown</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-white/5 text-slate-400 text-[11px] font-semibold uppercase tracking-wider">
                      <th className="py-3 px-6">ISP</th>
                      {SES_COLS.map(col => <SesColHeader key={col.label} {...col} />)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-white/5 text-sm text-slate-600 dark:text-slate-300">
                    {sesIspStats.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-8 text-slate-500 text-xs">No ISP data for this period.</td></tr>
                    ) : sesIspStats.map((row: any) => (
                      <tr key={row.isp} className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                        <td className="py-3.5 px-6 font-semibold text-slate-700 dark:text-slate-200">{row.isp}</td>
                        <td className="py-3.5 px-4 text-right tabular-nums">{(row.send || 0).toLocaleString()}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.deliveryRate, 'text-emerald-500')}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.transientBounceRate, 'text-slate-500', 5)}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.permanentBounceRate, 'text-slate-500', 5)}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.complaintRate, 'text-slate-500', 0.1)}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.openRate, 'text-primary-500')}</td>
                        <td className="py-3.5 px-4 text-right">{rateCell(row.clickRate, 'text-cyan-500')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'campaign-reports' && <div className="space-y-6 animate-fade-in">

      {/* Campaign Selector */}
      <div className="glass-panel rounded-2xl p-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex flex-col gap-1.5 w-full md:max-w-md">
          <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Select Campaign Report</label>
          <div ref={searchRef} className="relative">
            <div ref={inputWrapRef} className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={inputDisplayValue}
                onChange={(e) => { setSearchInput(e.target.value); updateRect(); setDropdownOpen(true); }}
                onFocus={() => { updateRect(); setDropdownOpen(true); setSearchInput(''); }}
                className="form-input bg-white dark:bg-slate-900 w-full pl-8 pr-8"
              />
              <ChevronDown
                size={14}
                className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
              />
            </div>

            {dropdownOpen && dropdownRect && createPortal(
              <div
                id="campaign-dropdown-portal"
                style={{
                  position: 'fixed',
                  top: dropdownRect.bottom + 4,
                  left: dropdownRect.left,
                  width: dropdownRect.width,
                  zIndex: 9999
                }}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-xl shadow-xl max-h-64 overflow-y-auto"
              >
                {filteredCampaigns.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-slate-400">No campaigns found</div>
                ) : filteredCampaigns.map((c: any) => (
                  <button
                    key={c.id}
                    onMouseDown={() => {
                      setSelectedCampaignId(c.id);
                      setDropdownOpen(false);
                      setSearchInput('');
                      setLogPage(1);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors border-b border-slate-100 dark:border-white/5 last:border-0 ${
                      c.id === selectedCampaignId ? 'bg-primary-500/10 text-primary-500' : 'text-slate-700 dark:text-slate-200'
                    }`}
                  >
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-slate-400">{new Date(c.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          c.status === 'SENT' ? 'bg-emerald-500' :
                          c.status === 'PROCESSING' ? 'bg-amber-500' :
                          c.status === 'SCHEDULED' ? 'bg-primary-500' :
                          'bg-slate-400'
                        }`} />
                        <span className="text-slate-400">{c.status}</span>
                      </span>
                    </div>
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        </div>

        {selectedCampaignId && (
          <button
            onClick={handleExportCSV}
            className="btn-secondary self-end flex items-center justify-center gap-2"
          >
            <Download size={16} />
            Export Campaign CSV
          </button>
        )}
      </div>

      {selectedCampaignId && isLoading && (
        <div className="py-12 text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <span className="text-xs text-slate-500 block mt-3">Compiling logs...</span>
        </div>
      )}

      {error && (
        <div className="p-6 bg-accent-rose/10 border border-accent-rose/20 rounded-xl text-accent-rose text-sm">
          Failed to load analytics report data.
        </div>
      )}

      {!selectedCampaignId && dashboardStats && (
        <div className="space-y-6 animate-fade-in">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-panel rounded-2xl p-5 flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Sent</span>
              <span className="text-2xl font-bold text-slate-900 dark:text-white">{(dashboardStats.counters?.sent || 0).toLocaleString()}</span>
            </div>
            <div className="glass-panel rounded-2xl p-5 flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Delivery Rate</span>
              <span className="text-2xl font-bold text-emerald-500">{(dashboardStats.counters?.rates?.deliveryRate || 0).toFixed(1)}%</span>
            </div>
            <div className="glass-panel rounded-2xl p-5 flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Open Rate</span>
              <span className="text-2xl font-bold text-primary-500">{(dashboardStats.counters?.rates?.openRate || 0).toFixed(1)}%</span>
            </div>
            <div className="glass-panel rounded-2xl p-5 flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Click Rate</span>
              <span className="text-2xl font-bold text-cyan-500">{(dashboardStats.counters?.rates?.clickRate || 0).toFixed(1)}%</span>
            </div>
          </div>

          {(() => {
            const sentCampaigns = [...campaigns]
              .filter((c: any) => (c.stats?.sent || 0) > 0 && !/test/i.test(c.name))
              .sort((a: any, b: any) => {
                const aRate = a.stats?.delivered > 0 ? (a.stats.opened / a.stats.delivered) : 0;
                const bRate = b.stats?.delivered > 0 ? (b.stats.opened / b.stats.delivered) : 0;
                return bRate - aRate;
              });
            if (sentCampaigns.length === 0) return null;
            return (
              <div className="glass-panel rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200 dark:border-white/5">
                  <h4 className="text-xs font-bold text-slate-800 dark:text-white uppercase tracking-wider">Top Campaigns by Open Rate</h4>
                </div>
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-white/5 text-slate-400 text-[11px] font-semibold uppercase tracking-wider">
                      <th className="py-3 px-6">Rank</th>
                      <th className="py-3 px-4">Campaign</th>
                      <th className="py-3 px-4 text-right">Sent</th>
                      <th className="py-3 px-4 text-right">Delivered</th>
                      <th className="py-3 px-4 text-right">Open Rate</th>
                      <th className="py-3 px-4 text-right">Click Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                    {sentCampaigns.map((c: any, i: number) => {
                      const sent = c.stats?.sent || 0;
                      const delivered = c.stats?.delivered || 0;
                      const openRate = delivered > 0 ? ((c.stats.opened / delivered) * 100).toFixed(1) : '0.0';
                      const clickRate = delivered > 0 ? ((c.stats.clicked / delivered) * 100).toFixed(1) : '0.0';
                      const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
                      return (
                        <tr
                          key={c.id}
                          onClick={() => { setSelectedCampaignId(c.id); setLogPage(1); }}
                          className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer group"
                        >
                          <td className="py-3.5 px-6 text-sm font-bold text-slate-400">
                            {medal ? <span>{medal}</span> : <span className="text-xs">#{i + 1}</span>}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className="text-sm font-semibold text-slate-800 dark:text-white group-hover:text-primary-500 transition-colors">{c.name}</span>
                          </td>
                          <td className="py-3.5 px-4 text-right text-sm text-slate-500 tabular-nums">{sent.toLocaleString()}</td>
                          <td className="py-3.5 px-4 text-right text-sm text-slate-500 tabular-nums">{delivered.toLocaleString()}</td>
                          <td className="py-3.5 px-4 text-right text-sm font-semibold text-primary-500 tabular-nums">{openRate}%</td>
                          <td className="py-3.5 px-4 text-right text-sm font-semibold text-cyan-500 tabular-nums">{clickRate}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {!selectedCampaignId && !dashboardStats && (
        <div className="glass-panel rounded-2xl p-12 text-center text-slate-500 flex flex-col justify-center items-center gap-3">
          <BarChart size={36} className="text-slate-600" />
          <p className="text-sm">Search and select a campaign above to view detailed deliverability reports.</p>
        </div>
      )}

      {selectedCampaignId && !isLoading && analyticsData && (
        <div className="space-y-6 animate-fade-in">
          {/* Campaign Details */}
          <div className="glass-panel rounded-2xl p-6">
            <h3 className="font-bold text-slate-800 dark:text-white text-lg mb-4">{campaign.name} Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-slate-500 block">Subject line</span>
                <span className="text-slate-700 dark:text-slate-200 font-semibold">{campaign.subject}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Target List</span>
                <span className="text-slate-700 dark:text-slate-200 font-semibold">{(campaign.recipientListId as any)?.name}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Sender Email</span>
                <span className="text-slate-700 dark:text-slate-200 font-semibold">{campaign.senderEmail}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Status</span>
                <span className="text-slate-700 dark:text-slate-200 font-semibold uppercase">{campaign.status}</span>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <StatsCard title="Delivered" value={stats.delivered || 0} rate={stats.rates?.deliveryRate || 0} rateLabel="Delivery Rate" icon={CheckCircle} color="emerald" />
            <StatsCard title="Opens" value={stats.opened || 0} rate={stats.rates?.openRate || 0} rateLabel="Open Rate" icon={Eye} color="blue" />
            <StatsCard title="Clicks" value={stats.clicked || 0} rate={stats.rates?.clickRate || 0} rateLabel="Click Rate" icon={MousePointerClick} color="violet" />
            <StatsCard title="Bounces" value={stats.bounced || 0} rate={stats.rates?.bounceRate || 0} rateLabel="Bounce Rate" icon={AlertCircle} color="rose" />
            <StatsCard title="Complaints" value={stats.complained || 0} rate={stats.rates?.complaintRate || 0} rateLabel="Complaint Rate" icon={AlertCircle} color="rose" />
            <StatsCard title="Unsubscribes" value={stats.unsubscribed || 0} rate={stats.rates?.unsubscribeRate || 0} rateLabel="Unsub Rate" icon={UserMinus} color="rose" />
          </div>

          {/* Charts & Funnel */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {/* Bar Chart — flex-col so chart fills panel height */}
            <div className="lg:col-span-2 glass-panel rounded-2xl p-6 flex flex-col min-h-[420px]">
              <h4 className="text-xs font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2 shrink-0">
                <TrendingUp size={16} className="text-primary-500" />
                Delivery Funnel Overview
              </h4>
              <div className="flex-1 min-h-0 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsBarChart data={chartData} margin={{ top: 28, right: 16, left: -20, bottom: 0 }} barCategoryGap="28%">
                    <CartesianGrid strokeDasharray="3 3" stroke={'rgba(0,0,0,0.05)'} vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip
                      cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        borderColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '10px',
                        fontSize: 12,
                        color: '#0f172a'
                      }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                      <LabelList dataKey="value" position="top" style={labelStyle} formatter={(v: number) => v.toLocaleString()} />
                    </Bar>
                  </RechartsBarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Conversion Funnel */}
            <div className="glass-panel rounded-2xl p-6 flex flex-col">
              <h4 className="text-xs font-bold text-slate-800 dark:text-white uppercase tracking-wider mb-5 shrink-0">Conversion Funnel</h4>
              <div className="flex-1 flex flex-col justify-center gap-0">
                {funnelSteps.map((step, i) => {
                  // cube-root scale — clamp min width to 55% so text is always readable
                  const rawPct = sentCount > 0 ? Math.cbrt(step.count / sentCount) * 100 : 100;
                  const visualPct = Math.max(rawPct, 55);
                  const marginH = (100 - visualPct) / 2;
                  const conv = step.prevCount && step.prevCount > 0
                    ? ((step.count / step.prevCount) * 100).toFixed(1)
                    : null;

                  return (
                    <React.Fragment key={step.label}>
                      {i > 0 && (
                        <div className="flex flex-col items-center py-1">
                          <div className="w-px h-2 bg-slate-300 dark:bg-slate-600" />
                          <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full my-0.5 whitespace-nowrap">
                            ↓ {conv !== null ? `${conv}%` : '—'} {step.convLabel}
                          </span>
                          <div className="w-px h-2 bg-slate-300 dark:bg-slate-600" />
                        </div>
                      )}
                      <div
                        className={`${step.bg} rounded-xl py-3 px-4 flex items-center justify-between min-w-0`}
                        style={{ marginLeft: `${marginH}%`, marginRight: `${marginH}%` }}
                      >
                        <div className="min-w-0 mr-3">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-white/90 truncate">{step.label}</div>
                          {step.base != null && step.base > 0 && (
                            <div className="text-[10px] text-white/70 mt-0.5 whitespace-nowrap">
                              {((step.count / step.base) * 100).toFixed(1)}% {step.baseLabel}
                            </div>
                          )}
                        </div>
                        <div className="text-xl font-bold text-white shrink-0">{step.count.toLocaleString()}</div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recipient Transmission Logs */}
          <div className="glass-panel rounded-2xl p-6 overflow-hidden">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-6 border-b border-slate-200/50 dark:border-white/5 pb-4">
              <h4 className="text-xs font-bold text-slate-800 dark:text-white uppercase tracking-wider flex items-center gap-2">
                <FileSpreadsheet size={16} className="text-primary-500" />
                Recipient Transmission Logs
              </h4>

              <div className="flex flex-col gap-3 w-full md:w-auto">
                <input
                  type="text"
                  placeholder="Search email..."
                  value={logSearch}
                  onChange={handleSearchChange}
                  className="form-input text-xs py-1.5 px-3 w-full sm:w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl"
                />
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: 'All', value: '' },
                    { label: 'Delivered', value: 'DELIVERED' },
                    { label: 'Opened', value: 'OPENED' },
                    { label: 'Clicked', value: 'CLICKED' },
                    { label: 'Bounced', value: 'BOUNCED' },
                    { label: 'Failed', value: 'FAILED' },
                  ].map(pill => (
                    <button
                      key={pill.value}
                      onClick={() => { setLogStatus(pill.value); setLogPage(1); }}
                      className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${
                        logStatus === pill.value
                          ? 'bg-primary-500/15 text-primary-600 dark:text-primary-400 border-primary-500/40'
                          : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:border-primary-300 dark:hover:border-white/20'
                      }`}
                    >
                      {pill.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-400 text-xs font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Recipient</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Opened / Clicked</th>
                    <th className="py-3 px-4">Sent Time</th>
                    <th className="py-3 px-4">Errors / Diagnostics</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-white/5 text-sm text-slate-600 dark:text-slate-300">
                  {logs.map((log: any) => {
                    const badgeColor = {
                      PENDING: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
                      SENT: 'bg-primary-500/10 text-primary-500 border-primary-500/20',
                      DELIVERED: 'bg-emerald-500/10 text-emerald-650 dark:text-accent-emerald border-emerald-500/20',
                      OPENED: 'bg-primary-500/10 text-primary-500 dark:text-primary-500 border-primary-500/20',
                      CLICKED: 'bg-primary-500/10 text-primary-500 dark:text-primary-500 border-primary-500/20',
                      BOUNCED: 'bg-rose-500/10 text-rose-555 dark:text-accent-rose border-rose-500/20',
                      COMPLAINED: 'bg-rose-500/10 text-rose-555 dark:text-accent-rose border-rose-500/20'
                    }[log.status as string] || 'bg-slate-105 dark:bg-slate-800 text-slate-600 dark:text-slate-400';

                    const recipient = log.recipientId || {};
                    const wasOpened = !!log.openedAt || log.status === 'OPENED' || log.status === 'CLICKED';
                    const wasClicked = !!log.clickedAt || log.status === 'CLICKED';

                    return (
                      <tr key={log.id || log._id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                        <td
                          className="py-3 px-4 cursor-pointer group"
                          onClick={() => recipient.email && handleCopyEmail(recipient.email, log.id || log._id)}
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-slate-800 dark:text-white">{recipient.email || '--'}</span>
                            {recipient.email && (
                              copiedId === (log.id || log._id)
                                ? <Check size={12} className="text-emerald-500 shrink-0" />
                                : <Copy size={12} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {recipient.firstName || recipient.lastName ? `${recipient.firstName || ''} ${recipient.lastName || ''}` : '--'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${badgeColor}`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-xs">
                            Opened:{' '}
                            <strong className={wasOpened ? 'text-accent-emerald' : 'text-slate-500'}>
                              {wasOpened ? 'Yes' : 'No'}
                            </strong>
                          </div>
                          <div className="text-xs">
                            Clicked:{' '}
                            <strong className={wasClicked ? 'text-accent-emerald' : 'text-slate-500'}>
                              {wasClicked ? 'Yes' : 'No'}
                            </strong>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-500">
                          {log.sentAt ? new Date(log.sentAt).toLocaleString() : '--'}
                        </td>
                        <td className="py-3 px-4 text-xs text-accent-rose max-w-xs truncate">
                          {log.error || '--'}
                        </td>
                      </tr>
                    );
                  })}

                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-slate-500">
                        No delivery logs recorded for this campaign yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-slate-200 dark:border-white/5 flex items-center justify-between">
                <span className="text-xs text-slate-400">
                  Showing page {logPage} of {pagination.pages}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={logPage === 1}
                    onClick={() => setLogPage(p => p - 1)}
                    className="btn-secondary px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
                  >
                    <ChevronLeft size={14} />
                    Prev
                  </button>
                  <button
                    disabled={logPage === pagination.pages}
                    onClick={() => setLogPage(p => p + 1)}
                    className="btn-secondary px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>}
    </div>
  );
};
export default Analytics;
